import { z } from 'zod';

import { PdfDocumentTemplateConfigSchema } from './notifications.js';

/**
 * The public read side of the booking PDF wording.
 *
 * The flight e-ticket and the hotel voucher are drawn in the traveller's
 * browser (see `apps/website/lib/tickets/`), which is what keeps them vector
 * sharp and lets a hotel booking still resolving its confirmation number
 * produce a correct document a poll later. The FACTS on those pages come from
 * the booking session the browser already holds; only the WORDING - what the
 * document calls itself, its section headings, its small print - is operator
 * owned, and that is what this endpoint serves.
 *
 * One unauthenticated GET returns every document's wording at once rather than
 * one call per booking: the payload is a few kilobytes, identical for every
 * traveller, and fetching it per download would put an API round trip between
 * pressing Download and getting a file.
 *
 * Placeholders are returned UNRESOLVED (`{{reference}}`, `{{pnr}}`, ...). The
 * browser holds the booking and substitutes them, so the response stays the
 * same for everyone and carries nobody's booking details.
 */

/**
 * The documents an operator can word, keyed by the notification event whose
 * template carries them. Event codes double as document codes deliberately -
 * one vocabulary for "the flight was confirmed", whether that produces an
 * email or a PDF.
 */
export const BOOKING_DOCUMENT_CODES = {
  FLIGHT_TICKET: 'booking.flight.confirmed',
  FLIGHT_CANCELLATION_REQUESTED: 'booking.flight.cancellation_requested',
  FLIGHT_CANCELLED: 'booking.flight.cancelled',
  HOTEL_VOUCHER: 'booking.hotel.confirmed',
  HOTEL_CANCELLATION_REQUESTED: 'booking.hotel.cancellation_requested',
  HOTEL_CANCELLED: 'booking.hotel.cancelled',
} as const;

export type BookingDocumentCode =
  (typeof BOOKING_DOCUMENT_CODES)[keyof typeof BOOKING_DOCUMENT_CODES];

export const BOOKING_DOCUMENT_CODE_LIST = Object.values(
  BOOKING_DOCUMENT_CODES,
) as readonly BookingDocumentCode[];

/// One document's wording. Same shape the admin Templates tab edits.
export const BookingDocumentCopySchema = PdfDocumentTemplateConfigSchema;
export type BookingDocumentCopy = z.infer<typeof BookingDocumentCopySchema>;

/**
 * Keyed by document code. A code is ABSENT when its template is inactive or
 * has been deleted - the browser then draws the document with its own built-in
 * wording, which is why pausing a document template degrades to the shipped
 * copy instead of producing a blank voucher.
 */
export const BookingDocumentTemplatesResponseSchema = z.object({
  data: z.record(z.string(), BookingDocumentCopySchema),
});
export type BookingDocumentTemplatesResponse = z.infer<
  typeof BookingDocumentTemplatesResponseSchema
>;
